import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const TryCatchConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['settings', 'error']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-3">
      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="Try-Catch Beállítások"
        icon="🛡️"
        color="amber"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="catchEnabled"
              checked={node.data.config.catchEnabled !== false}
              onChange={(e) => updateConfig('catchEnabled', e.target.checked)}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
            />
            <label htmlFor="catchEnabled" className="text-sm text-gray-700 dark:text-gray-300">
              Hibakezelés engedélyezése
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="continueOnError"
              checked={node.data.config.continueOnError || false}
              onChange={(e) => updateConfig('continueOnError', e.target.checked)}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
            />
            <label htmlFor="continueOnError" className="text-sm text-gray-700 dark:text-gray-300">
              Folytatás hiba esetén
            </label>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
            <div className="text-xs text-amber-700 dark:text-amber-300">
              <strong>💡 Működés:</strong> A Try-Catch blokk elkapja a hibákat és két kimenetre irányítja őket: <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">success</code> (sikeres) és <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">error</code> (hiba).
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Hibaüzenet */}
      <CollapsibleSection
        id="error"
        title="Hibaüzenet Kezelés"
        icon="📝"
        color="red"
        isExpanded={expandedSections.has('error')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Egyéni hibaüzenet
            </label>
            <textarea
              value={node.data.config.customErrorMessage || ''}
              onChange={(e) => updateConfig('customErrorMessage', e.target.value)}
              className={commonStyles.textarea}
              rows={3}
              placeholder="Hiba történt: {{error.message}}"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Opcionális egyéni hibaüzenet változókkal
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="includeStackTrace"
              checked={node.data.config.includeStackTrace || false}
              onChange={(e) => updateConfig('includeStackTrace', e.target.checked)}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="includeStackTrace" className="text-sm text-gray-700 dark:text-gray-300">
              Stack trace hozzáadása
            </label>
          </div>
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A Try-Catch után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{result}}', desc: 'Sikeres eredmény', example: '{...}', output: 'success' },
              { var: '{{error}}', desc: 'Hiba objektum', example: '{message: "..."}', output: 'error' },
              { var: '{{error.message}}', desc: 'Hibaüzenet', example: 'Connection failed', output: 'error' },
              { var: '{{error.type}}', desc: 'Hiba típusa', example: 'NetworkError', output: 'error' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {item.output}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>✅ API hívás védelem:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Try-Catch → [try] HTTP Request → [success] Response<br/>
              Try-Catch → [catch] Error Logger → Notification
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🗄️ Database védelem:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Try-Catch → Database Query → [error] Fallback Action
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🔄 Komplex flow:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Try-Catch → Multiple Actions → [error] Rollback
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default TryCatchConfig;
